import { CONFIG } from "../config";
import { DEFAULT_PDF_PAGE_SLUG, normalizePdfPageSlug } from "./pdfRouting";

const FILES_BASE = CONFIG.IMAGES_BASE;
const DEFAULT_VIEWER_HEIGHT = 700;

export const DEFAULT_PDF_STYLE_CONFIG = Object.freeze({
  applyToAllCards: true,
  sectionTitleColor: "#0f172a",
  sectionTitleSize: 36,
  sectionSubtitleColor: "#475569",
  sectionSubtitleSize: 14,
  cardTitleColor: "#0f172a",
  cardTitleSize: 17,
  cardDescriptionColor: "#475569",
  cardDescriptionSize: 13,
  viewerTitleColor: "#0f172a",
  viewerTitleSize: 20,
  viewerDescriptionColor: "#475569",
  viewerDescriptionSize: 13,
});

export const PDF_PLACEMENT_TYPES = Object.freeze({
  PAGE: "page",
  SECTION: "section",
});

export const PDF_SECTION_PAGE_OPTIONS = Object.freeze([
  "home",
  "contact",
  "about",
  "reviews",
]);

const isNonEmptyString = (value) =>
  typeof value === "string" && value.trim() !== "";

const clampNumber = (value, min, max, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

const normalizeColor = (value, fallback) => {
  if (!isNonEmptyString(value)) return fallback;
  return value.trim();
};

const toFileUrl = (value) => {
  if (!isNonEmptyString(value)) return "";
  const normalized = value.trim();
  if (/^https?:\/\//i.test(normalized) || normalized.startsWith("//")) {
    return normalized;
  }
  if (normalized.startsWith("/")) return normalized;
  return `${FILES_BASE}/${encodeURIComponent(normalized)}`;
};

const normalizeSectionPage = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "");
  return normalized || "home";
};

const normalizePlacementType = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (normalized === PDF_PLACEMENT_TYPES.PAGE) return PDF_PLACEMENT_TYPES.PAGE;
  if (normalized === PDF_PLACEMENT_TYPES.SECTION) return PDF_PLACEMENT_TYPES.SECTION;
  return PDF_PLACEMENT_TYPES.PAGE;
};

const createStableId = (value, index) => {
  if (isNonEmptyString(value?.id)) return value.id.trim();
  return `pdf-${index + 1}`;
};

export const createPdfDocumentDraft = (index = 0) => ({
  id: `pdf-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`,
  enabled: true,
  title: "",
  description: "",
  buttonText: "Download PDF",
  pageTitle: "Documents",
  pageDescription: "",
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
  pageSlug: DEFAULT_PDF_PAGE_SLUG,
  pdfFileName: "",
  externalUrl: "",
  placementType: PDF_PLACEMENT_TYPES.PAGE,
  sectionPage: "home",
  sectionOrder: index + 1,
  viewerHeight: DEFAULT_VIEWER_HEIGHT,
  cardTitleColor: DEFAULT_PDF_STYLE_CONFIG.cardTitleColor,
  cardTitleSize: DEFAULT_PDF_STYLE_CONFIG.cardTitleSize,
  cardDescriptionColor: DEFAULT_PDF_STYLE_CONFIG.cardDescriptionColor,
  cardDescriptionSize: DEFAULT_PDF_STYLE_CONFIG.cardDescriptionSize,
});

export const normalizePdfStyleConfig = (value) => {
  const incoming = value && typeof value === "object" ? value : {};

  return {
    applyToAllCards:
      incoming.applyToAllCards === undefined ? true : Boolean(incoming.applyToAllCards),
    sectionTitleColor: normalizeColor(
      incoming.sectionTitleColor,
      DEFAULT_PDF_STYLE_CONFIG.sectionTitleColor
    ),
    sectionTitleSize: clampNumber(
      incoming.sectionTitleSize,
      18,
      64,
      DEFAULT_PDF_STYLE_CONFIG.sectionTitleSize
    ),
    sectionSubtitleColor: normalizeColor(
      incoming.sectionSubtitleColor,
      DEFAULT_PDF_STYLE_CONFIG.sectionSubtitleColor
    ),
    sectionSubtitleSize: clampNumber(
      incoming.sectionSubtitleSize,
      10,
      30,
      DEFAULT_PDF_STYLE_CONFIG.sectionSubtitleSize
    ),
    cardTitleColor: normalizeColor(
      incoming.cardTitleColor,
      DEFAULT_PDF_STYLE_CONFIG.cardTitleColor
    ),
    cardTitleSize: clampNumber(
      incoming.cardTitleSize,
      12,
      42,
      DEFAULT_PDF_STYLE_CONFIG.cardTitleSize
    ),
    cardDescriptionColor: normalizeColor(
      incoming.cardDescriptionColor,
      DEFAULT_PDF_STYLE_CONFIG.cardDescriptionColor
    ),
    cardDescriptionSize: clampNumber(
      incoming.cardDescriptionSize,
      10,
      30,
      DEFAULT_PDF_STYLE_CONFIG.cardDescriptionSize
    ),
    viewerTitleColor: normalizeColor(
      incoming.viewerTitleColor,
      DEFAULT_PDF_STYLE_CONFIG.viewerTitleColor
    ),
    viewerTitleSize: clampNumber(
      incoming.viewerTitleSize,
      14,
      48,
      DEFAULT_PDF_STYLE_CONFIG.viewerTitleSize
    ),
    viewerDescriptionColor: normalizeColor(
      incoming.viewerDescriptionColor,
      DEFAULT_PDF_STYLE_CONFIG.viewerDescriptionColor
    ),
    viewerDescriptionSize: clampNumber(
      incoming.viewerDescriptionSize,
      10,
      30,
      DEFAULT_PDF_STYLE_CONFIG.viewerDescriptionSize
    ),
  };
};

export const normalizePdfDocument = (value, index, legacyEnabled = true) => {
  const incoming = value && typeof value === "object" ? value : {};
  const cardStyleSource =
    incoming.cardStyle && typeof incoming.cardStyle === "object"
      ? incoming.cardStyle
      : {};

  const uploadedFileName = isNonEmptyString(incoming.pdfFileName)
    ? incoming.pdfFileName.trim()
    : "";
  const externalUrl = isNonEmptyString(incoming.externalUrl)
    ? incoming.externalUrl.trim()
    : "";
  const directPdfUrl = isNonEmptyString(incoming.pdfUrl) ? incoming.pdfUrl.trim() : "";

  const explicitPlacement = incoming.placementType;
  const inferredPlacement =
    incoming.showInHome || incoming.showInContact
      ? PDF_PLACEMENT_TYPES.SECTION
      : PDF_PLACEMENT_TYPES.PAGE;

  const placementType = normalizePlacementType(explicitPlacement || inferredPlacement);
  const sectionPage = normalizeSectionPage(
    incoming.sectionPage ||
      incoming.pageName ||
      (incoming.showInContact ? "contact" : "home")
  );
  const sectionOrder = clampNumber(
    incoming.sectionOrder ?? incoming.order ?? index + 1,
    0,
    9999,
    index + 1
  );

  return {
    id: createStableId(incoming, index),
    enabled:
      incoming.enabled === undefined ? Boolean(legacyEnabled) : Boolean(incoming.enabled),
    title: isNonEmptyString(incoming.title)
      ? incoming.title.trim()
      : `PDF Document ${index + 1}`,
    description: isNonEmptyString(incoming.description)
      ? incoming.description.trim()
      : "",
    buttonText: isNonEmptyString(incoming.buttonText)
      ? incoming.buttonText.trim()
      : "Download PDF",
    pageTitle: isNonEmptyString(incoming.pageTitle)
      ? incoming.pageTitle.trim()
      : "Documents",
    pageDescription: isNonEmptyString(incoming.pageDescription)
      ? incoming.pageDescription.trim()
      : "",
    metaTitle: isNonEmptyString(incoming.metaTitle)
      ? incoming.metaTitle.trim()
      : "",
    metaDescription: isNonEmptyString(incoming.metaDescription)
      ? incoming.metaDescription.trim()
      : "",
    metaKeywords: isNonEmptyString(incoming.metaKeywords)
      ? incoming.metaKeywords.trim()
      : "",
    pageSlug: normalizePdfPageSlug(incoming.pageSlug),
    pdfFileName: uploadedFileName,
    externalUrl,
    pdfUrl: externalUrl || toFileUrl(uploadedFileName) || directPdfUrl,
    placementType,
    sectionPage,
    sectionOrder,
    viewerHeight: clampNumber(
      incoming.viewerHeight,
      320,
      1600,
      DEFAULT_VIEWER_HEIGHT
    ),
    cardTitleColor: normalizeColor(
      incoming.cardTitleColor ?? cardStyleSource.cardTitleColor,
      DEFAULT_PDF_STYLE_CONFIG.cardTitleColor
    ),
    cardTitleSize: clampNumber(
      incoming.cardTitleSize ?? cardStyleSource.cardTitleSize,
      12,
      42,
      DEFAULT_PDF_STYLE_CONFIG.cardTitleSize
    ),
    cardDescriptionColor: normalizeColor(
      incoming.cardDescriptionColor ?? cardStyleSource.cardDescriptionColor,
      DEFAULT_PDF_STYLE_CONFIG.cardDescriptionColor
    ),
    cardDescriptionSize: clampNumber(
      incoming.cardDescriptionSize ?? cardStyleSource.cardDescriptionSize,
      10,
      30,
      DEFAULT_PDF_STYLE_CONFIG.cardDescriptionSize
    ),
    updatedAt: incoming.updatedAt || null,
  };
};

const normalizeLegacyDocuments = (incoming) => {
  const placements = [];
  const legacyEnabled = Boolean(incoming?.enabled);

  if (incoming?.showAsPage) {
    placements.push({ ...incoming, placementType: PDF_PLACEMENT_TYPES.PAGE });
  }
  if (incoming?.showInHome) {
    placements.push({
      ...incoming,
      placementType: PDF_PLACEMENT_TYPES.SECTION,
      sectionPage: "home",
    });
  }
  if (incoming?.showInContact) {
    placements.push({
      ...incoming,
      placementType: PDF_PLACEMENT_TYPES.SECTION,
      sectionPage: "contact",
    });
  }

  const hasPdfSource =
    isNonEmptyString(incoming?.pdfFileName) ||
    isNonEmptyString(incoming?.externalUrl) ||
    isNonEmptyString(incoming?.pdfUrl);

  if (!placements.length && hasPdfSource) {
    placements.push({ ...incoming, placementType: PDF_PLACEMENT_TYPES.PAGE });
  }

  return placements.map((item, index) =>
    normalizePdfDocument(item, index, legacyEnabled)
  );
};

export const normalizePdfConfig = (value) => {
  const incoming = value && typeof value === "object" ? value : {};
  const styleSource =
    incoming.styleConfig && typeof incoming.styleConfig === "object"
      ? incoming.styleConfig
      : incoming;
  const docsSource = Array.isArray(incoming.documents)
    ? incoming.documents
    : normalizeLegacyDocuments(incoming);

  return {
    documents: docsSource.map((doc, index) => normalizePdfDocument(doc, index, true)),
    styleConfig: normalizePdfStyleConfig(styleSource),
    updatedAt: incoming.updatedAt || null,
  };
};

const sortByOrder = (a, b) => {
  const byOrder = Number(a.sectionOrder || 0) - Number(b.sectionOrder || 0);
  if (byOrder !== 0) return byOrder;
  return String(a.title || "").localeCompare(String(b.title || ""));
};

export const getPdfDocumentsForSection = (documents, sectionPage) => {
  const normalizedPage = normalizeSectionPage(sectionPage);
  const list = Array.isArray(documents) ? documents : [];

  return list
    .filter(
      (doc) =>
        doc?.enabled &&
        doc?.placementType === PDF_PLACEMENT_TYPES.SECTION &&
        normalizeSectionPage(doc?.sectionPage) === normalizedPage &&
        isNonEmptyString(doc?.pdfUrl)
    )
    .sort(sortByOrder);
};

export const getPdfDocumentsForPageSlug = (documents, pageSlug) => {
  const normalizedSlug = normalizePdfPageSlug(pageSlug);
  const list = Array.isArray(documents) ? documents : [];

  return list
    .filter(
      (doc) =>
        doc?.enabled &&
        doc?.placementType === PDF_PLACEMENT_TYPES.PAGE &&
        normalizePdfPageSlug(doc?.pageSlug) === normalizedSlug &&
        isNonEmptyString(doc?.pdfUrl)
    )
    .sort(sortByOrder);
};

export const getPdfPageSlugsFromDocuments = (documents) => {
  const list = Array.isArray(documents) ? documents : [];
  const slugs = new Set();

  list.forEach((doc) => {
    if (!doc?.enabled) return;
    if (doc?.placementType !== PDF_PLACEMENT_TYPES.PAGE) return;
    if (!isNonEmptyString(doc?.pdfUrl)) return;
    slugs.add(normalizePdfPageSlug(doc?.pageSlug));
  });

  return Array.from(slugs);
};

export const getPdfPageTitleFromDocuments = (documents, pageSlug) => {
  const docs = getPdfDocumentsForPageSlug(documents, pageSlug);
  return docs[0]?.pageTitle || "Documents";
};

export const getPdfPageDescriptionFromDocuments = (documents, pageSlug) => {
  const docs = getPdfDocumentsForPageSlug(documents, pageSlug);
  return docs[0]?.pageDescription || "";
};

export const getPdfPageMetaFromDocuments = (documents, pageSlug) => {
  const docs = getPdfDocumentsForPageSlug(documents, pageSlug);
  const primary = docs[0] || {};
  return {
    metaTitle: primary.metaTitle || "",
    metaDescription: primary.metaDescription || "",
    metaKeywords: primary.metaKeywords || "",
  };
};
